<template>
  <div id="member-portal" ref="mainBody">
    <template v-if="contentsLoaded === true && userLoggedin === true">
      <MemberPortalNavigation :headerData="headerData" :memberDetailsProp="memberDetails" />
      <main>
        <Breadcrumb :includeIcon="false" />
        <router-view />
        <!-- webChat button -->
        <!-- Hiding legacy web chat component as part of DE2E-9365 -->
        <!-- <LiveChat /> -->
      </main>
      <FooterComponent :footerData="footerData" />

      <ModalPopUp :modalActive="showInactiveWarningPopup" :backgroundCloseModal="true" header="Session expiring" @closeBackgroundModal="sessionContinue">
        <template v-slot:modalContent>
          <div role="dialog">
            <p id="session-timeout-desc">Your session on the Defence Health Member Portal will expire soon. Click the button below to refresh your session and continue.</p>
            <button class="secondary" @click.self="sessionContinue">Continue my session</button>
          </div>
        </template>
      </ModalPopUp>
      <ModalPopUp :modalActive="showUnderMaintenanceWarningPopup" :backgroundCloseModal="true" header="Under maintenance" @closeBackgroundModal="logout" :allowClose="false">
        <template v-slot:modalContent>
          <div role="dialog">
            <p id="session-timeout-desc" v-html="maintenanceAlertText"></p>
            <button class="secondary" @click.self="logout">Logout</button>
          </div>
        </template>
      </ModalPopUp>
    </template>

    <template v-else>
      <MaintenanceMode v-if="underMaintenance" />
      <ApiLoading v-else loadingMessage="Your Member Portal is loading now." :apiError="contentsLoadedFailed" />
    </template>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import cloneDeep from 'lodash.clonedeep';
import CommonServices from '@/services/Common';
import MemberDetailsServices from '@/services/MemberDetails';
import { ApiLoading, FooterComponent, MemberPortalNavigation, ModalPopUp } from '@/components';
import { AccessLevel, NonDependantFlexCodes, DeviceTypes } from '@/enums';
// import LiveChat from '@general/js/components/LiveChat.vue';
import MaintenanceMode from '@general/js/components/MaintenanceMode.vue';
import Breadcrumb from '@/components/Breadcrumb.vue';
import { VUEX_GENERAL } from '@/constants/VuexConstants';
import { deviceType } from '@/utils';
import { DHLError } from '@general/js/utils';
import memberPortalAutoLogout from './memberPortalAutoLogoutMixin';

export default {
  mixins: [memberPortalAutoLogout],

  components: {
    ApiLoading,
    Breadcrumb,
    FooterComponent,
    /* Hiding legacy web chat component as part of DE2E-9365 */
    // LiveChat,
    MemberPortalNavigation,
    MaintenanceMode,
    ModalPopUp,
  },

  data() {
    // TODO: The defaulting of these objects will need to be considered inside NavigationComponent.Vue
    return {
      backupNavItems: null,
      headerData: { logo: '' },
      footerData: { dhlLogo: '' },
      contentsLoaded: false,
      contentsLoadedFailed: false,
      isInDev: process.env.NODE_ENV === 'development',
      coveredAs: '',
      accessLevel: '',
      currentDevice: null,
      underMaintenance: false,
    };
  },

  computed: {
    ...mapGetters({
      memberships: VUEX_GENERAL.GETTERS.memberships,
    }),
  },

  watch: {
    $route() {
      if (!this.isInDev) {
        logger.log('reset all counter and reset state');
        clearTimeout(this.inActiveCounter);
        clearTimeout(this.autoLogoutCounter);
        clearTimeout(this.debounceCounter);
        this.debounceCounter = null;

        this.userInactiveTimer();
        this.showInactiveWarningPopup = false;
      }
    },

    policyNumber() {
      this.$nextTick(() => {
        this.updateNavItems();
      });
    },
  },

  created() {
    // In case user put url to access the member portal and bypass policy selection.
    this.saveThisPolicyNumber(this.$route.params.policyNumber);

    // Set user's current device
    this.currentDevice = deviceType();
  },

  methods: {
    async getNavigationAndMemberDetails() {
      this.contentsLoaded = false;
      this.contentsLoadedFailed = false;

      try {
        const data = await CommonServices.getNavigationAndMemberDetails();
        // Check if maintenance mode is on
        if (data.underMaintenance) {
          this.underMaintenance = true;
          // stop further loading if under maintenance (CRM/OHI/EAI deployments in progress)
          throw new DHLError('Under maintenance!');
        }
        if (data.memberDetailsInformation?.signinEmailAddress){
          console.log('Identifying user ' + data.memberDetailsInformation?.signinEmailAddress);
          try {
            dtrum?.identifyUser(data.memberDetailsInformation?.signinEmailAddress);
          }
          catch (error) {
            logger.error('Error identifying user in DT session: ' + error);
          }
        }

        const memberships = await MemberDetailsServices.getMemberships();

        // save membership
        this.saveThisMembership(memberships);

        // save member details on vuex
        this.updateCurrentMemberDetails({
          firstName: data.memberDetailsInformation.firstName[0].toUpperCase() + data.memberDetailsInformation.firstName.slice(1).toLowerCase(),
          middleName: data.memberDetailsInformation.middleName
            ? data.memberDetailsInformation.middleName[0].toUpperCase() + data.memberDetailsInformation.middleName.slice(1).toLowerCase()
            : '',
          lastName: data.memberDetailsInformation.lastName[0].toUpperCase() + data.memberDetailsInformation.lastName.slice(1).toLowerCase(),
          isFraudulent: data.memberDetailsInformation.isFraudulent,
          contactId: data.memberDetailsInformation.contactId,
          memberships: data.memberDetailsInformation.memberships,
          memberItems: data.memberDetailsMenu,
          personCode: data.memberDetailsInformation.personCode,
        });
        this.headerData = data.header;
        this.backupNavItems = data.header.menuItems;
        this.footerData = data.footer;
        this.contentsLoaded = true;

        // save policy number if there is only one policy
        if (memberships.productList.length === 1) {
          this.saveThisPolicyNumber(memberships.productList[0].memberNumber);
        }

        this.updateNavItems();
      } catch (error) {
        this.contentsLoadedFailed = true;
        logger.error(error);
      }
    },

    updateNavItems() {
      const currentMembership = this.memberships?.find((membership) => {
        return membership.memberNumber === this.policyNumber;
      });

      // save coveredAs and accessLevel info
      if (currentMembership) {
        if (currentMembership.coveredAs && currentMembership.accessLevel) {
          this.saveThisPolicyNumber(currentMembership.memberNumber);
          this.coveredAs = currentMembership.coveredAs;
          this.accessLevel = currentMembership.accessLevel;
        }
      } else {
        this.saveThisPolicyNumber();
        this.coveredAs = '';
        this.accessLevel = '';
      }

      const inaccessiblePages = [];
      const nonDependantList = Object.values(NonDependantFlexCodes);

      // Hide "snap and send" from CMS when device = desktop
      if (this.currentDevice === DeviceTypes.DESKTOP) {
        inaccessiblePages.push('snap and send');
      }

      // Remove navigation items that should not be accessed when policy does not have extras
      if (this.currentIsExtrasFlag === false) {
        // DE2E-13230: CMS changed 'make a claim' to 'express claim', had to update here to disallow page access
        inaccessiblePages.push('benefit quotes', 'express claim', 'my limits and usage');
      }

      // Remove navigation items if product is extras only
      if (this.currentExtrasOnlyFlag) {
        inaccessiblePages.push('change my excess');
      }

      // Remove navigation items that should not be accessed when member is a dependant and/or has self access level
      if (this.accessLevel.toLowerCase() === AccessLevel.SELF_ACCESS) {
        if (!nonDependantList.includes(this.coveredAs.toUpperCase())) {
          inaccessiblePages.push('tax statements');
        }
        inaccessiblePages.push('premium calculator', 'change my excess', 'rebate');
      }

      // Remove navigation items that should not be accessed
      if (inaccessiblePages.length !== 0) {
        if (this.headerData.menuItems) {
          this.headerData.menuItems.forEach((lvl0) => {
            // Only include nav item if not part of inaccessible pages
            lvl0.children = lvl0.children.filter((lvl1) => {
              return !inaccessiblePages.includes(lvl1.text.toLowerCase());
            });

            // Filter also the inner children links
            if (lvl0.children && lvl0.children.length > 0) {
              lvl0.children.forEach((lvl1) => {
                lvl1.children = lvl1.children.filter((lvl2) => {
                  return !inaccessiblePages.includes(lvl2.text.toLowerCase());
                });
              });
            }
          });
        }
      } else {
        this.headerData.menuItems = cloneDeep(this.backupNavItems);
      }
    },
  },
};
</script>

<style lang="scss">
@import '@general/styles/components/_webchat.scss';

#member-portal {
  margin: 0;
  display: flex;
  flex-direction: column;
  min-height: 100vh;

  & > .api-loading-wrapper {
    height: 100vh;
  }

  main {
    position: relative;

    #main-content {
      display: flex;
      flex-direction: column;
    }

    // breadcrumb
    .breadcrumbs {
      @include main-content;
    }

    // Prevents multiple <a> elements from being focused on simultaneously during keyboard navigation (caused by the inherited `display: flex` property).
    a {
      display: inline-block;
    }

    // To overwrite general h1 style for member portal
    h1 {
      margin-left: 0;
      margin-right: 0;
      float: none;
    }

    // To overwrite general table background image for member portal
    table {
      background-image: none;

      // Unique review table style for member portal
      &.review-table {
        width: 100%;

        thead {
          tr {
            th {
              font-weight: $BOLD;
              padding: spacer(1) spacer(2);
              border-top: 1px solid $GREY_700;
              border-bottom: 1px solid $GREY_700;
            }
          }
        }

        tbody {
          tr {
            th,
            td {
              vertical-align: top;
              border-bottom: 0;
              padding: spacer(1) spacer(2) 0;
              width: 50%;
            }
          }
        }
      }
    }

    // required indicator global style
    .required-indicator {
      font-size: font-size(-2);

      span {
        font-weight: $BOLD;
        color: $CRIMSON;
      }
    }
  }

  .modal {
    .x {
      position: absolute;
      top: 0px;
      right: 0px;
      font-size: 0;
      width: 30px;
      height: 30px;
      min-height: unset;
      border-radius: 50%;
      box-shadow: none;
      background: $GREY_500;
      min-width: unset;
      padding: unset;
      margin: 12px;

      &:before,
      &:after {
        position: absolute;
        content: '';
        display: block;
        height: 2px;
        width: 70%;
        background: $GREY_600;
        transform-origin: center;
        top: 50%;
        left: 50%;
        border-radius: 5px;
      }

      &:before {
        transform: translate(-50%, -50%) rotate(45deg);
      }

      &:after {
        transform: translate(-50%, -50%) rotate(-45deg);
      }

      &:hover,
      &:focus {
        &:before,
        &:after {
          background: $CRIMSON;
        }
      }
    }
  }
}
</style>
